/**
 * Club Olay Member Pricing - Dynamic Price Updates
 * 
 * This module handles dynamic price calculations and updates for Club Olay members.
 * It integrates with variant selection, OrderGroove subscriptions, and cart updates.
 * 
 * @version 1.0.0
 */

(function() {
  'use strict';

  const ClubOlayPricing = {
    
    /**
     * Initialize Club Olay pricing functionality
     */
    init() {
      this.isClubMember = this.checkClubMembership();
      
      if (!this.isClubMember) {
        return; // Not a Club Olay member, no need to initialize
      }

      this.bindEvents();
      this.initializeProductPages();
      this.integrateWithOrderGroove();
    },

    /**
     * Check if current customer is a Club Olay member
     * @returns {boolean}
     */
    checkClubMembership() {
      // Check if customer is logged in
      if (typeof window.ShopifyAnalytics !== 'undefined' && 
          window.ShopifyAnalytics.meta && 
          window.ShopifyAnalytics.meta.page &&
          window.ShopifyAnalytics.meta.page.customerId) {
        
        // Customer is logged in, check for Club Olay tag
        // The Liquid template should set this data attribute
        const body = document.body;
        return body.dataset.clubOlayMember === 'true';
      }
      
      return false;
    },

    /**
     * Bind event listeners
     */
    bindEvents() {
      // Listen for variant changes
      document.addEventListener('variant:change', this.handleVariantChange.bind(this));
      
      // Listen for custom variant change events (different themes use different events)
      document.addEventListener('variantChange', this.handleVariantChange.bind(this));
      
      // Listen for OrderGroove subscription toggle
      document.addEventListener('ordergroove:subscription:toggle', this.handleOrderGrooveToggle.bind(this));
      
      // Listen for cart updates
      document.addEventListener('cart:updated', this.handleCartUpdate.bind(this));
      
      // Standard variant selector change
      const variantSelects = document.querySelectorAll('[name="id"], .product-form__variants');
      variantSelects.forEach(select => {
        select.addEventListener('change', (e) => {
          this.handleVariantChangeFromSelect(e.target);
        });
      });
    },

    /**
     * Initialize pricing on product pages
     */
    initializeProductPages() {
      // Update prices on page load
      const priceElements = document.querySelectorAll('[data-price-display]');
      priceElements.forEach(element => {
        const productId = element.closest('[data-product-id]')?.dataset.productId;
        if (productId) {
          this.updatePriceDisplay(element, productId);
        }
      });
    },

    /**
     * Handle variant change event
     * @param {Event} event
     */
    handleVariantChange(event) {
      const variant = event.detail?.variant;
      if (!variant) return;

      this.updatePricesForVariant(variant);
    },

    /**
     * Handle variant change from select element
     * @param {HTMLElement} selectElement
     */
    handleVariantChangeFromSelect(selectElement) {
      const variantId = selectElement.value;
      const productElement = selectElement.closest('[data-product-id]');
      
      if (!productElement) return;

      const productId = productElement.dataset.productId;
      
      // Fetch variant data
      this.fetchVariantData(productId, variantId).then(variant => {
        if (variant) {
          this.updatePricesForVariant(variant);
        }
      });
    },

    /**
     * Fetch variant data from Shopify
     * @param {string} productId
     * @param {string} variantId
     * @returns {Promise<Object>}
     */
    async fetchVariantData(productId, variantId) {
      try {
        const response = await fetch(`/products/${productId}?variant=${variantId}&view=json`);
        if (!response.ok) throw new Error('Failed to fetch variant data');
        
        const data = await response.json();
        return data.variant;
      } catch (error) {
        console.error('[Club Olay] Error fetching variant:', error);
        return null;
      }
    },

    /**
     * Update prices for a specific variant
     * @param {Object} variant
     */
    updatePricesForVariant(variant) {
      const productElement = document.querySelector(`[data-product-id="${variant.product_id}"]`);
      if (!productElement) return;

      // Get Club Olay discount metafields
      const discountType = productElement.dataset.clubOlayDiscountType;
      const discountValue = parseFloat(productElement.dataset.clubOlayDiscount) || 0;

      if (!discountType || !discountValue) {
        // No Club Olay discount, show regular price
        this.showRegularPrice(productElement, variant);
        return;
      }

      // Calculate Club Olay discounted price
      const pricing = this.calculateDiscount(variant.price, discountType, discountValue);
      
      // Update price display
      this.updatePriceElements(productElement, {
        originalPrice: variant.price,
        discountedPrice: pricing.discountedPrice,
        savings: pricing.savings,
        discountPercent: pricing.discountPercent,
        compareAtPrice: variant.compare_at_price
      });
    },

    /**
     * Calculate discount based on type and value
     * @param {number} price - Price in cents
     * @param {string} discountType - 'percentage' or 'fixed'
     * @param {number} discountValue - Discount amount
     * @returns {Object}
     */
    calculateDiscount(price, discountType, discountValue) {
      let discountedPrice = price;
      let savings = 0;

      if (discountType === 'percentage') {
        savings = Math.round(price * (discountValue / 100));
        discountedPrice = price - savings;
      } else if (discountType === 'fixed') {
        // Fixed discount is in dollars, convert to cents
        savings = Math.round(discountValue * 100);
        discountedPrice = Math.max(0, price - savings);
      }

      const discountPercent = price > 0 ? Math.round((savings / price) * 100) : 0;

      return {
        discountedPrice: Math.max(0, discountedPrice),
        savings: savings,
        discountPercent: discountPercent
      };
    },

    /**
     * Update price display elements
     * @param {HTMLElement} container
     * @param {Object} priceData
     */
    updatePriceElements(container, priceData) {
      const priceDisplay = container.querySelector('[data-price-display]');
      const comparePrice = container.querySelector('[data-price-compare]');
      const savingsElement = container.querySelector('.club-olay-savings');

      if (priceDisplay) {
        priceDisplay.textContent = this.formatMoney(priceData.discountedPrice);
      }

      if (comparePrice) {
        comparePrice.textContent = this.formatMoney(priceData.originalPrice);
        comparePrice.style.display = 'block';
      }

      if (savingsElement) {
        const savingsText = `Save ${this.formatMoney(priceData.savings)} (${priceData.discountPercent}% off)`;
        savingsElement.textContent = savingsText;
        savingsElement.style.display = 'inline-block';
      }

      // Add visual indicator
      container.classList.add('club-olay-pricing-active');
    },

    /**
     * Show regular price (no Club Olay discount)
     * @param {HTMLElement} container
     * @param {Object} variant
     */
    showRegularPrice(container, variant) {
      const priceDisplay = container.querySelector('[data-price-display]');
      const comparePrice = container.querySelector('[data-price-compare]');
      const savingsElement = container.querySelector('.club-olay-savings');

      if (priceDisplay) {
        priceDisplay.textContent = this.formatMoney(variant.price);
      }

      if (comparePrice && variant.compare_at_price > variant.price) {
        comparePrice.textContent = this.formatMoney(variant.compare_at_price);
        comparePrice.style.display = 'block';
      } else if (comparePrice) {
        comparePrice.style.display = 'none';
      }

      if (savingsElement) {
        savingsElement.style.display = 'none';
      }

      container.classList.remove('club-olay-pricing-active');
    },

    /**
     * Format money using Shopify's money format
     * @param {number} cents
     * @returns {string}
     */
    formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}}');
      }
      
      // Fallback formatting
      const dollars = (cents / 100).toFixed(2);
      return `$${dollars}`;
    },

    /**
     * Integrate with OrderGroove subscription widget
     */
    integrateWithOrderGroove() {
      // Wait for OrderGroove to initialize
      if (typeof window.OG === 'undefined') {
        // OrderGroove not loaded yet, wait for it
        const checkOrderGroove = setInterval(() => {
          if (typeof window.OG !== 'undefined') {
            clearInterval(checkOrderGroove);
            this.setupOrderGrooveIntegration();
          }
        }, 500);
        
        // Stop checking after 10 seconds
        setTimeout(() => clearInterval(checkOrderGroove), 10000);
      } else {
        this.setupOrderGrooveIntegration();
      }
    },

    /**
     * Setup OrderGroove integration
     */
    setupOrderGrooveIntegration() {
      // OrderGroove applies its own discount on top of Club Olay pricing
      // The Cart Transform function handles the base price update
      // OrderGroove subscription discount will be applied to the transformed price
      
      // Listen for OrderGroove subscription toggle
      document.addEventListener('click', (e) => {
        const subscriptionToggle = e.target.closest('[data-ordergroove-subscription-toggle]');
        if (subscriptionToggle) {
          setTimeout(() => {
            this.refreshPricing();
          }, 100);
        }
      });
    },

    /**
     * Handle OrderGroove subscription toggle
     * @param {Event} event
     */
    handleOrderGrooveToggle(event) {
      this.refreshPricing();
    },

    /**
     * Refresh all pricing on the page
     */
    refreshPricing() {
      const productElements = document.querySelectorAll('[data-product-id]');
      productElements.forEach(element => {
        const productId = element.dataset.productId;
        if (productId) {
          this.updatePriceDisplay(element, productId);
        }
      });
    },

    /**
     * Update price display for a specific element
     * @param {HTMLElement} element
     * @param {string} productId
     */
    updatePriceDisplay(element, productId) {
      const priceCalculatorData = element.querySelector('.club-olay-price-data');
      if (!priceCalculatorData) return;

      const isActive = priceCalculatorData.dataset.clubOlayActive === 'true';
      
      if (isActive) {
        const pricing = {
          originalPrice: parseInt(priceCalculatorData.dataset.clubOlayOriginalPrice),
          discountedPrice: parseInt(priceCalculatorData.dataset.clubOlayDiscountedPrice),
          savings: parseInt(priceCalculatorData.dataset.clubOlaySavings),
          discountPercent: parseInt(priceCalculatorData.dataset.clubOlayDiscountPercentage)
        };
        
        this.updatePriceElements(element, pricing);
      }
    },

    /**
     * Handle cart update events
     * @param {Event} event
     */
    handleCartUpdate(event) {
      // Cart Transform function handles the price updates in cart
      // This is just for potential future enhancements
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => ClubOlayPricing.init());
  } else {
    ClubOlayPricing.init();
  }

  // Expose for external access if needed
  window.ClubOlayPricing = ClubOlayPricing;

})();
